/**
 * UndoRedoToolbar - Undo/redo buttons for board toolbar
 *
 * Per CONTEXT.md decisions:
 * - Undo/redo buttons in toolbar
 * - Buttons disabled (grayed out) when nothing to undo/redo
 * - Brief highlight on affected area when undo/redo happens (TODO: Phase 4/5)
 *
 * Keyboard shortcuts (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z) are handled
 * by useUndoShortcuts hook separately.
 */

interface UndoRedoToolbarProps {
  // Whether undo is available
  canUndo: boolean;
  // Whether redo is available
  canRedo: boolean;
  // Undo handler
  onUndo: () => void;
  // Redo handler
  onRedo: () => void;
  // Optional: show stack sizes
  showStackSize?: boolean;
  undoStackSize?: number;
  redoStackSize?: number;
}

export function UndoRedoToolbar({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  showStackSize = false,
  undoStackSize = 0,
  redoStackSize = 0,
}: UndoRedoToolbarProps) {
  return (
    <div className="flex items-center gap-1 rounded-lg border bg-white p-1 shadow-sm">
      {/* Undo button */}
      <button
        onClick={onUndo}
        disabled={!canUndo}
        className="flex items-center gap-1 rounded px-2 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-gray-100"
        title="Undo (Cmd/Ctrl+Z)"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
          />
        </svg>
        {showStackSize && undoStackSize > 0 && (
          <span className="text-xs text-gray-500">({undoStackSize})</span>
        )}
      </button>

      {/* Separator */}
      <div className="h-6 w-px bg-gray-200" />

      {/* Redo button */}
      <button
        onClick={onRedo}
        disabled={!canRedo}
        className="flex items-center gap-1 rounded px-2 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:bg-gray-100"
        title="Redo (Cmd/Ctrl+Shift+Z)"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6"
          />
        </svg>
        {showStackSize && redoStackSize > 0 && (
          <span className="text-xs text-gray-500">({redoStackSize})</span>
        )}
      </button>
    </div>
  );
}
